import { navMain, type MenuItemType } from "@/config/menu";
import { ChevronRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "./ui/sidebar";
import { useAuth } from "@/modules/authentication/context/AuthContext";
import { hasPermission } from "@/modules/authentication/permissions";

interface NavMainProps {
  items: typeof navMain;
}

export function NavMain({ items }: NavMainProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (url?: string) => {
    if (url) navigate(url);
  };

  const checkAccess = (item: MenuItemType): boolean => hasPermission(user, item.permission);

  const getVisibleChildren = (item: MenuItemType) =>
    (item.items ?? []).filter(child => checkAccess(child));

  const isSubItemActive = (url: string): boolean => location.pathname === url;

  const isMenuItemActive = (item: MenuItemType): boolean => {
    const currentPath = location.pathname;

    if (item.url && currentPath === item.url) return true;

    return (item.items ?? []).some(subItem => currentPath === subItem.url);
  };

  return (
    <SidebarGroup>
      <SidebarMenu>
        {items.map((item) => {
          const visibleChildren = getVisibleChildren(item);
          const hasChildren = visibleChildren.length > 0;
          const hasAccessToItem = hasChildren || checkAccess(item);
          const isActive = isMenuItemActive(item);

          if (!hasAccessToItem) return null;

          return hasChildren ? (
            <Collapsible
              key={item.title}
              asChild
              defaultOpen={isActive}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip={item.title}
                    size="lg"
                    onClick={() => handleNavigation(item.url)}
                    className={`h-12 text-gray-700 hover:!bg-emerald-50 hover:!text-emerald-700 data-[state=open]:bg-emerald-50 data-[state=open]:text-emerald-700 rounded-lg transition-all duration-300 text-base font-medium px-4 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center cursor-pointer shadow-sm hover:shadow-md ${
                      isActive ? 'bg-emerald-500 text-white shadow-lg' : 'hover:bg-gray-100'
                    }`}
                  >
                    {item.icon && <item.icon className="w-5 h-5 group-data-[collapsible=icon]:w-4 group-data-[collapsible=icon]:h-4 transition-transform duration-200 group-hover:scale-110" />}
                    <span className="flex-1 text-left group-data-[collapsible=icon]:hidden font-medium">{item.title}</span>
                    <ChevronRight className="ml-auto w-4 h-4 transition-transform duration-300 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub className="ml-6 mt-2 space-y-1 group-data-[collapsible=icon]:hidden">
                    {visibleChildren.map((subItem) => {
                      const isSubActive = isSubItemActive(subItem.url);
                      return (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton
                            onClick={() => handleNavigation(subItem.url)}
                            className={`h-10 text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 rounded-md transition-all duration-300 text-sm px-3 cursor-pointer shadow-sm hover:shadow-md ${
                              isSubActive ? 'bg-emerald-100 text-emerald-700 font-medium shadow-md' : 'hover:bg-gray-50'
                            }`}
                          >
                            {subItem.icon && <subItem.icon className="mr-2 size-4" />}
                            <span>{subItem.title}</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      );
                    })}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          ) : (
            <SidebarMenuItem key={item.title}>
              <span className={`w-2 h-[80%] bg-primary rounded-r-lg absolute -left-2 top-1/2 -translate-y-1/2 ${isActive ? 'block' : 'hidden'} group-data-[collapsible=icon]:hidden`}></span>
              <SidebarMenuButton
                tooltip={item.title}
                onClick={() => handleNavigation(item.url)}
                size="lg"
                className={`h-12 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 rounded-0 transition-all duration-300 text-base font-medium pl-6 px-4 group-data-[collapsible=icon]:h-10 group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center cursor-pointer relative ${
                  isActive ? ' font-semibold group-data-[collapsible=icon]:bg-primary group-data-[collapsible=icon]:text-white' : 'hover:bg-gray-100'
                }`}
              >
                {item.icon && <item.icon className="size-6 group-data-[collapsible=icon]:w-4 group-data-[collapsible=icon]:h-4 transition-transform duration-200 group-hover:scale-110" />}
                <span className="text-sm text-left group-data-[collapsible=icon]:hidden font-medium">{item.title}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
